import * as d3 from 'd3';
import { TreeNode, LayoutResult, LayoutEdge, StyleOptions, TanglegramStyle, TreeEditAction } from './types';
import { computeLayout, RECT_MARGIN_LEFT, RECT_MARGIN_RIGHT, RECT_MARGIN_TOP, RECT_MARGIN_BOTTOM } from './layout';
import { pruneNode, pruneNodes, extractSubtree, rerootAt, ladderize } from './newick-parser';
import type { TipColorMap, MetadataTable } from './metadata';
import { recolorForVisibleTips } from './metadata';

export interface TanglegramOptions {
  container: HTMLElement;
  tree1: TreeNode;
  tree2: TreeNode;
  style: StyleOptions;
  tanglegramStyle: TanglegramStyle;
  onNodeEdit?: (newRoot: TreeNode, action: TreeEditAction, treeIndex: 1 | 2) => void;
  tipColorMap?: TipColorMap | null;
  /** Full metadata table for tooltips */
  metadataTable?: MetadataTable | null;
  /** Which column in the metadata table holds tip IDs */
  metadataIdColumn?: string;
  darkMode?: boolean;
  boxSelectMode?: boolean;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const DEFAULT_TREE_COLOR = '#1b1b1b';
const DEFAULT_TREE_COLOR_DARK = '#e0e0e0';
function themedColor(userValue: string, darkMode: boolean): string {
  if (darkMode && userValue === DEFAULT_TREE_COLOR) return DEFAULT_TREE_COLOR_DARK;
  return userValue;
}

/**
 * Render a tanglegram: two trees facing each other with
 * connecting lines between matching leaf labels.
 */
export class TanglegramRenderer {
  private svg!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private g!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private zoom!: d3.ZoomBehavior<SVGSVGElement, unknown>;
  private container: HTMLElement;
  private contextMenu: HTMLElement | null = null;
  private tooltip: HTMLElement | null = null;
  private dismissContextMenuBound = () => this.dismissContextMenu();
  private boxSelectMode: boolean = false;
  private selectedNodes1: Set<TreeNode> = new Set();
  private selectedNodes2: Set<TreeNode> = new Set();
  private boxSelectStart: { svgX: number; svgY: number } | null = null;
  private boxSelectRectEl: d3.Selection<SVGRectElement, unknown, null, undefined> | null = null;
  private lastLayout1: LayoutResult | null = null;
  private lastLayout2: LayoutResult | null = null;
  private onSvgMouseDownBound = (e: MouseEvent) => this.onSvgBoxSelectMouseDown(e);
  private onWindowMouseMoveBound = (e: MouseEvent) => this.onBoxSelectMouseMove(e);
  private onWindowMouseUpBound = (e: MouseEvent) => this.onBoxSelectMouseUp(e);
  private onKeyDownBound = (e: KeyboardEvent) => this.onBoxSelectKeyDown(e);

  constructor(private options: TanglegramOptions) {
    this.container = options.container;
    this.boxSelectMode = !!options.boxSelectMode;
    this.init();
    this.render();
  }

  updateTree(index: 1 | 2, newRoot: TreeNode): void {
    if (index === 1) this.options.tree1 = newRoot;
    else this.options.tree2 = newRoot;
  }

  private init(): void {
    d3.select(this.container).selectAll('*').remove();

    this.svg = d3.select(this.container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('class', 'tanglegram-svg');

    this.g = this.svg.append('g').attr('class', 'tanglegram-group');

    this.zoom = d3.zoom<SVGSVGElement, unknown>()
      .scaleExtent([0.1, 20])
      .on('zoom', (event) => {
        this.g.attr('transform', event.transform);
      });

    this.zoom.filter((event: Event) => {
      if (this.boxSelectMode) return event.type === 'wheel';
      const me = event as MouseEvent;
      return !me.ctrlKey && !me.button;
    });

    this.svg.call(this.zoom);

    this.boxSelectRectEl = this.svg.append('rect')
      .attr('class', 'box-select-rect')
      .attr('fill', 'rgba(0,94,162,0.07)')
      .attr('stroke', '#005ea2')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '5,3')
      .attr('pointer-events', 'none')
      .attr('display', 'none');

    this.svg.node()!.addEventListener('mousedown', this.onSvgMouseDownBound, true);
    window.addEventListener('mousemove', this.onWindowMouseMoveBound);
    window.addEventListener('mouseup', this.onWindowMouseUpBound);
    document.addEventListener('keydown', this.onKeyDownBound);

    this.svg.on('contextmenu.boxselect', (event: MouseEvent) => {
      if (!this.boxSelectMode) return;
      const total = this.selectedNodes1.size + this.selectedNodes2.size;
      if (total === 0) return;
      event.preventDefault();
      this.showBulkContextMenu(event.clientX, event.clientY);
    });

    if (this.boxSelectMode) {
      this.svg.style('cursor', 'crosshair');
    }
  }

  render(): void {
    this.g.selectAll('*').remove();

    const { tree1, tree2, style, tanglegramStyle } = this.options;
    const containerRect = this.container.getBoundingClientRect();
    const autoWidth = containerRect.width || 1200;
    const autoHeight = containerRect.height || 800;
    const totalWidth = style.canvasWidth > 0 ? style.canvasWidth : autoWidth;
    const totalHeight = style.canvasHeight > 0 ? style.canvasHeight : autoHeight;

    // Use spacing setting to control gap between trees
    const gapFraction = Math.max(0.05, Math.min(0.6, tanglegramStyle.spacing));
    const treeSpace = totalWidth * (1 - gapFraction);
    const gapWidth = totalWidth * gapFraction;

    // Minimum tree width: must exceed layout margins so plotWidth stays positive.
    // RECT_MARGIN_LEFT + RECT_MARGIN_RIGHT reserve space for labels; add 40px for branches.
    const MIN_TREE_WIDTH = RECT_MARGIN_LEFT + RECT_MARGIN_RIGHT + 40; // 200px
    const MIN_TREE_HEIGHT = RECT_MARGIN_TOP + RECT_MARGIN_BOTTOM + 40; // 80px

    // Fold-change width balance: 2^s fraction goes to left, remainder to right
    const sw = tanglegramStyle.widthScaler ?? 0;
    const leftWidthFrac = Math.pow(2, sw) / (Math.pow(2, sw) + 1);
    const rawLeftWidth = treeSpace * leftWidthFrac;
    // Clamp so neither tree drops below MIN_TREE_WIDTH. If treeSpace is too narrow
    // for two minimum-width trees, each tree overflows its half — the user can zoom
    // out to see both, but the layout will never invert.
    const leftTreeWidth = Math.max(MIN_TREE_WIDTH, Math.min(rawLeftWidth, treeSpace - MIN_TREE_WIDTH));
    const rightTreeWidth = Math.max(MIN_TREE_WIDTH, treeSpace - leftTreeWidth);

    // Fold-change height balance: each tree gets an independent height
    const sh = tanglegramStyle.heightScaler ?? 0;
    const leftHeightFrac = Math.pow(2, sh) / (Math.pow(2, sh) + 1);
    const leftTreeHeight = Math.max(totalHeight * 2 * leftHeightFrac, MIN_TREE_HEIGHT);
    const rightTreeHeight = Math.max(totalHeight * 2 * (1 - leftHeightFrac), MIN_TREE_HEIGHT);

    // Layout left tree (normal)
    const layout1 = computeLayout(tree1, 'rectangular', leftTreeWidth, leftTreeHeight);

    // Layout right tree (mirrored)
    const layout2 = computeLayout(tree2, 'rectangular', rightTreeWidth, rightTreeHeight);

    // Mirror the right tree: flip X coordinates
    const rightOffset = leftTreeWidth + gapWidth;
    for (const node of layout2.nodes) {
      node.x = rightOffset + (rightTreeWidth - node.x);
    }
    for (const edge of layout2.edges) {
      edge.sourceX = rightOffset + (rightTreeWidth - edge.sourceX);
      edge.targetX = rightOffset + (rightTreeWidth - edge.targetX);
      if (edge.elbowX !== undefined) {
        edge.elbowX = rightOffset + (rightTreeWidth - edge.elbowX);
      }
    }

    // Align right tree vertically to minimize average absolute offset of connections.
    // Build name→y maps for leaf nodes in each tree.
    const leftLeafY = new Map<string, number>();
    for (const n of layout1.nodes) {
      if (n.node.children.length === 0) leftLeafY.set(n.node.name, n.y);
    }
    const rightLeafY = new Map<string, number>();
    for (const n of layout2.nodes) {
      if (n.node.children.length === 0) rightLeafY.set(n.node.name, n.y);
    }
    const diffs: number[] = [];
    for (const [name, lY] of leftLeafY) {
      const rY = rightLeafY.get(name);
      if (rY !== undefined) diffs.push(lY - rY);
    }
    // Optimal offset for minimizing sum of absolute deviations = median of diffs.
    // Fallback when no shared leaves: align vertical midpoints.
    let yOffset: number;
    if (diffs.length === 0) {
      yOffset = leftTreeHeight / 2 - rightTreeHeight / 2;
    } else {
      diffs.sort((a, b) => a - b);
      yOffset = diffs[Math.floor(diffs.length / 2)];
    }
    for (const node of layout2.nodes) node.y += yOffset;
    for (const edge of layout2.edges) {
      edge.sourceY += yOffset;
      edge.targetY += yOffset;
      if (edge.elbowY !== undefined) edge.elbowY += yOffset;
    }

    // Recolor for visible categories across both trees for best palette distinguishability
    const rawTcm = this.options.tipColorMap;
    let activeTcm: TipColorMap | null = null;
    if (rawTcm && rawTcm.colorByTip.size > 0) {
      const allLeafNames = [...leftLeafY.keys(), ...rightLeafY.keys()];
      activeTcm = recolorForVisibleTips(rawTcm, allLeafNames);
    }

    // Store layouts for lasso hit-testing
    this.lastLayout1 = layout1;
    this.lastLayout2 = layout2;

    // Draw left tree, capture label end positions
    const leftLabelEnds = this.drawTree(layout1, style, 'left', 1, tanglegramStyle.showLeafLabels1, activeTcm);

    // Draw right tree, capture label end positions
    const rightLabelEnds = this.drawTree(layout2, style, 'right', 2, tanglegramStyle.showLeafLabels2, activeTcm);

    // Scale bars (rectangular layout has branch lengths)
    this.renderScaleBar(layout1, 'left', style);
    this.renderScaleBar(layout2, 'right', style);

    // Draw connecting lines between matching leaves
    this.drawConnections(leftLabelEnds, rightLabelEnds, tanglegramStyle);

    // Color legend — already filtered and recolored in activeTcm
    if (activeTcm && activeTcm.legend.length > 0) {
      this.renderLegend(activeTcm);
    }

    // Figure title — rendered last so getBBox captures all other content
    this.renderFigureTitle();

    document.addEventListener('click', this.dismissContextMenuBound);
  }

  private renderFigureTitle(): void {
    const titleText = this.options.style.figureTitle?.trim() ?? '';
    if (!titleText) return;
    const gNode = this.g.node();
    if (!gNode) return;
    const bbox = gNode.getBBox();
    const fontSize = this.options.style.figureTitleSize;
    this.g.append('text')
      .attr('class', 'figure-title')
      .attr('x', bbox.x + bbox.width / 2)
      .attr('y', bbox.y - fontSize * 0.5)
      .attr('text-anchor', 'middle')
      .attr('font-size', fontSize + 'px')
      .attr('font-family', this.options.style.fontFamily)
      .attr('font-weight', '700')
      .attr('fill', this.options.darkMode ? '#e0e0e0' : '#1b1b1b')
      .text(titleText);
  }

  private renderLegend(tcm: TipColorMap): void {
    const gNode = this.g.node();
    if (!gNode) return;
    const bbox = gNode.getBBox();
    const legendMargin = 30;
    const x0 = bbox.x + bbox.width + legendMargin;
    const fontSize = this.options.style.legendLabelSize;
    const rowHeight = fontSize + 6;
    const dotR = Math.max(3, Math.round(fontSize * 0.45));
    const titleText = this.options.style.legendTitle?.trim() ?? '';
    const titleHeight = titleText ? fontSize + 8 : 0;
    const y0 = bbox.y + 10 + titleHeight;

    const legendGroup = this.g.append('g').attr('class', 'legend');

    if (titleText) {
      legendGroup.append('text')
        .attr('x', x0)
        .attr('y', bbox.y + 10 + fontSize)
        .attr('font-size', fontSize + 'px')
        .attr('font-family', this.options.style.fontFamily)
        .attr('font-weight', '600')
        .attr('fill', this.options.darkMode ? '#e0e0e0' : '#1b1b1b')
        .text(titleText);
    }

    tcm.legend.forEach((item, i) => {
      const y = y0 + i * rowHeight + rowHeight / 2;
      legendGroup.append('circle')
        .attr('cx', x0)
        .attr('cy', y)
        .attr('r', dotR)
        .attr('fill', item.color);

      legendGroup.append('text')
        .attr('x', x0 + dotR + 8)
        .attr('y', y)
        .attr('dy', '0.35em')
        .attr('font-size', fontSize + 'px')
        .attr('font-family', this.options.style.fontFamily)
        .attr('fill', this.options.darkMode ? '#e0e0e0' : '#1b1b1b')
        .text(item.category);
    });

    const contentBBox = legendGroup.node()!.getBBox();
    const pad = 8;
    legendGroup.insert('rect', ':first-child')
      .attr('x', contentBBox.x - pad)
      .attr('y', contentBBox.y - pad)
      .attr('width', contentBBox.width + pad * 2)
      .attr('height', contentBBox.height + pad * 2)
      .attr('rx', 4)
      .attr('fill', this.options.darkMode ? '#1e1e1e' : '#ffffff')
      .attr('fill-opacity', 0.9)
      .attr('stroke', this.options.darkMode ? '#444' : '#dfe1e2')
      .attr('stroke-width', 1);
  }

  /**
   * Draw a tree and return a map of leaf name -> label end x-coordinate.
   * For left tree, label end is the right edge of the text.
   * For right tree, label end is the left edge of the text.
   */
  private drawTree(
    layout: LayoutResult,
    style: StyleOptions,
    side: 'left' | 'right',
    treeIndex: 1 | 2,
    showLabels: boolean,
    activeTcm: TipColorMap | null
  ): Map<string, { x: number; y: number }> {
    const group = this.g.append('g').attr('class', `tree-${side}`);

    const darkMode = !!this.options.darkMode;
    const branchColor = themedColor(style.branchColor, darkMode);
    const defaultLabelColor = themedColor(style.leafLabelColor, darkMode);
    const self = this;

    // Tip color lookup (uses recolored map from caller)
    const tcm = activeTcm?.colorByTip;
    const dnm = activeTcm?.displayNameByTip;
    const tipColor = (name: string): string => {
      if (!tcm) return defaultLabelColor;
      return tcm.get(name) ?? tcm.get(name.replace(/_/g, ' ')) ?? tcm.get(name.replace(/ /g, '_')) ?? defaultLabelColor;
    };
    const displayName = (name: string): string => {
      if (!dnm) return name.replace(/_/g, ' ');
      return dnm.get(name) ?? dnm.get(name.replace(/_/g, ' ')) ?? dnm.get(name.replace(/ /g, '_')) ?? name.replace(/_/g, ' ');
    };
    // Root node (parentX === null)
    const root = layout.nodes.find((n) => n.parentX === null)!.node;

    // Edges with elbow connectors
    group.selectAll('path.branch')
      .data(layout.edges)
      .enter()
      .append('path')
      .attr('class', 'branch')
      .attr('d', (d) => {
        if (d.elbowX !== undefined && d.elbowY !== undefined) {
          return `M${d.sourceX},${d.sourceY} V${d.elbowY} H${d.targetX}`;
        }
        return `M${d.sourceX},${d.sourceY} L${d.targetX},${d.targetY}`;
      })
      .attr('fill', 'none')
      .attr('stroke', branchColor)
      .attr('stroke-width', style.branchWidth);

    // Leaf labels
    const leafNodes = layout.nodes.filter((n) => n.node.children.length === 0);
    const hasTipColors = tcm && tcm.size > 0 && leafNodes.some(
      (n) => tcm.get(n.node.name) ?? tcm.get(n.node.name.replace(/_/g, ' ')) ?? tcm.get(n.node.name.replace(/ /g, '_'))
    );

    if (showLabels) {
      const labelSelection = group.selectAll('text.leaf-label')
        .data(leafNodes)
        .enter()
        .append('text')
        .attr('class', 'leaf-label')
        .attr('x', (d) => d.x + (side === 'left' ? 6 : -6))
        .attr('y', (d) => d.y)
        .attr('dy', '0.35em')
        .attr('text-anchor', side === 'left' ? 'start' : 'end')
        .attr('font-size', style.leafLabelSize + 'px')
        .attr('font-family', style.fontFamily)
        .attr('fill', (d) => tipColor(d.node.name))
        .attr('font-style', 'italic')
        .text((d) => displayName(d.node.name));

      labelSelection
        .on('mouseenter', (event: MouseEvent, d) => this.showTooltip(event, d.node))
        .on('mousemove', (event: MouseEvent) => this.moveTooltip(event))
        .on('mouseleave', () => this.hideTooltip());
    }

    // Leaf dots
    group.selectAll('circle.leaf-node')
      .data(leafNodes)
      .enter()
      .append('circle')
      .attr('class', 'leaf-node')
      .attr('cx', (d) => d.x)
      .attr('cy', (d) => d.y)
      .attr('r', hasTipColors ? 4 : 2)
      .attr('fill', (d) => tipColor(d.node.name));

    // Branch length labels
    if (style.showBranchLengths) {
      // Key edges by their target node so we can look up the already-mirrored coordinates
      const edgeByNode = new Map<TreeNode, LayoutEdge>();
      for (const e of layout.edges) edgeByNode.set(e.targetNode, e);

      const branchNodes = layout.nodes.filter(
        (n) => n.node.branchLength !== null && n.parentX !== null
      );
      group.selectAll('text.branch-length')
        .data(branchNodes)
        .enter()
        .append('text')
        .attr('class', 'branch-length')
        .attr('x', (d) => {
          const edge = edgeByNode.get(d.node);
          // Midpoint of the horizontal segment (elbowX → targetX)
          return edge ? ((edge.elbowX ?? edge.sourceX) + edge.targetX) / 2 : d.x;
        })
        .attr('y', (d) => d.y - 4)
        .attr('text-anchor', 'middle')
        .attr('font-size', (style.internalLabelSize - 1) + 'px')
        .attr('font-family', style.fontFamily)
        .attr('fill', darkMode ? '#888' : '#999')
        .text((d) => d.node.branchLength?.toFixed(4) ?? '');
    }

    // Interactive hit targets for all nodes (click internal = flip, right-click = context menu)
    const internalNodes = layout.nodes.filter((n) => n.node.children.length > 0);
    const allNodes = [...internalNodes, ...leafNodes];

    group.selectAll('circle.node-target')
      .data(allNodes)
      .enter()
      .append('circle')
      .attr('class', 'node-target')
      .attr('cx', (d) => d.x)
      .attr('cy', (d) => d.y)
      .attr('r', 6)
      .attr('fill', 'transparent')
      .attr('stroke', 'transparent')
      .attr('stroke-width', 1)
      .attr('cursor', 'pointer')
      .on('mouseenter', function () {
        const hoverFill = darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)';
        const hoverStroke = darkMode ? '#888' : '#71767a';
        d3.select(this).attr('fill', hoverFill).attr('stroke', hoverStroke);
      })
      .on('mouseleave', function () {
        d3.select(this).attr('fill', 'transparent').attr('stroke', 'transparent');
      })
      .on('click', function (_event, d) {
        if (d.node.children.length > 0) {
          d.node.children.reverse();
          self.options.onNodeEdit?.(root, 'flip', treeIndex);
        }
      })
      .on('contextmenu', function (event, d) {
        event.preventDefault();
        event.stopPropagation();
        if (self.boxSelectMode) {
          const total = self.selectedNodes1.size + self.selectedNodes2.size;
          if (total > 0) self.showBulkContextMenu(event.clientX, event.clientY);
          return;
        }
        self.showContextMenu(event.clientX, event.clientY, d.node, root, treeIndex);
      });

    // Measure label end positions for connection lines
    const labelEnds = new Map<string, { x: number; y: number }>();
    if (showLabels) {
      group.selectAll<SVGTextElement, typeof leafNodes[0]>('text.leaf-label').each(function (d) {
        const bbox = (this as SVGTextElement).getBBox();
        const endX = side === 'left'
          ? bbox.x + bbox.width + 3  // right edge + small pad
          : bbox.x - 3;              // left edge - small pad
        labelEnds.set(d.node.name, { x: endX, y: d.y });
      });
    } else {
      // No labels: connections attach at the tip node dot
      for (const n of leafNodes) {
        const endX = side === 'left' ? n.x + 3 : n.x - 3;
        labelEnds.set(n.node.name, { x: endX, y: n.y });
      }
    }

    return labelEnds;
  }

  private showContextMenu(x: number, y: number, node: TreeNode, root: TreeNode, treeIndex: 1 | 2): void {
    this.dismissContextMenu();
    const menu = document.createElement('div');
    menu.className = 'tree-context-menu';
    menu.style.left = x + 'px';
    menu.style.top = y + 'px';

    const isInternal = node.children.length > 0;
    const isRoot = node === root;
    const nodeName = node.name
      ? node.name.replace(/_/g, ' ')
      : (isInternal ? 'this clade' : 'this leaf');

    const items: { label: string; action: TreeEditAction }[] = [];

    if (isInternal) {
      items.push({ label: 'Flip children', action: 'flip' });
      items.push({ label: 'Ladderize (large first)', action: 'ladderize-desc' });
      items.push({ label: 'Ladderize (small first)', action: 'ladderize-asc' });
    }
    if (!isRoot) {
      items.push({ label: `Remove ${nodeName}`, action: 'prune' });
    }
    if (isInternal && !isRoot) {
      items.push({ label: `Keep only ${nodeName}`, action: 'keep' });
      items.push({ label: 'Reroot here', action: 'reroot' });
    }

    for (const item of items) {
      const el = document.createElement('div');
      el.className = 'tree-context-menu-item';
      el.textContent = item.label;
      el.addEventListener('click', () => {
        this.dismissContextMenu();
        this.executeAction(item.action, node, root, treeIndex);
      });
      menu.appendChild(el);
    }

    document.body.appendChild(menu);
    this.contextMenu = menu;

    requestAnimationFrame(() => {
      const rect = menu.getBoundingClientRect();
      if (rect.right > window.innerWidth) menu.style.left = (x - rect.width) + 'px';
      if (rect.bottom > window.innerHeight) menu.style.top = (y - rect.height) + 'px';
    });
  }

  private dismissContextMenu(): void {
    if (this.contextMenu) {
      this.contextMenu.remove();
      this.contextMenu = null;
    }
  }

  private executeAction(action: TreeEditAction, node: TreeNode, root: TreeNode, treeIndex: 1 | 2): void {
    const onNodeEdit = this.options.onNodeEdit;
    if (!onNodeEdit) return;

    let newRoot = root;
    switch (action) {
      case 'flip':
        node.children.reverse();
        break;
      case 'ladderize-desc':
        ladderize(node, false);
        break;
      case 'ladderize-asc':
        ladderize(node, true);
        break;
      case 'prune': {
        const result = pruneNode(root, node);
        if (!result) return;
        newRoot = result;
        break;
      }
      case 'keep':
        newRoot = extractSubtree(node);
        newRoot.branchLength = null;
        break;
      case 'reroot':
        newRoot = rerootAt(root, node);
        break;
    }
    onNodeEdit(newRoot, action, treeIndex);
  }

  private renderScaleBar(layout: LayoutResult, side: 'left' | 'right', style: StyleOptions): void {
    const hasBranchLengths = layout.nodes.some((n) => n.node.branchLength !== null);
    if (!hasBranchLengths) return;

    const rootNode = layout.nodes.find((n) => n.parentX === null);
    if (!rootNode) return;

    const leafNodes = layout.nodes.filter((n) => n.node.children.length === 0);

    // Left tree: root at min-x, leaves at max-x. Right tree (mirrored): root at max-x, leaves at min-x.
    let plotWidth: number;
    let barStartX: number;
    let barEndX: number;
    if (side === 'left') {
      const maxLeafX = leafNodes.reduce((max, n) => Math.max(max, n.x), 0);
      plotWidth = maxLeafX - rootNode.x;
      const totalBranchLen = this.getMaxBranchLengthFromTree(rootNode.node);
      if (plotWidth <= 0 || totalBranchLen <= 0) return;
      const scaleBarPx = this.niceRound(80 / (plotWidth / totalBranchLen)) * (plotWidth / totalBranchLen);
      barStartX = rootNode.x;
      barEndX = rootNode.x + scaleBarPx;
    } else {
      const minLeafX = leafNodes.reduce((min, n) => Math.min(min, n.x), Infinity);
      plotWidth = rootNode.x - minLeafX;
      const totalBranchLen = this.getMaxBranchLengthFromTree(rootNode.node);
      if (plotWidth <= 0 || totalBranchLen <= 0) return;
      const scaleBarPx = this.niceRound(80 / (plotWidth / totalBranchLen)) * (plotWidth / totalBranchLen);
      barStartX = rootNode.x - scaleBarPx;
      barEndX = rootNode.x;
    }

    const totalBranchLen = this.getMaxBranchLengthFromTree(rootNode.node);
    if (totalBranchLen <= 0) return;
    const pixelsPerUnit = plotWidth / totalBranchLen;
    const scaleBarValue = this.niceRound(80 / pixelsPerUnit);

    const maxLeafY = leafNodes.reduce((max, n) => Math.max(max, n.y), 0);
    const barY = maxLeafY + style.leafLabelSize / 2 + 20;
    const darkMode = !!this.options.darkMode;
    const scaleColor = themedColor(style.branchColor, darkMode);

    const scaleGroup = this.g.append('g').attr('class', `scale-bar-${side}`);

    scaleGroup.append('line')
      .attr('x1', barStartX).attr('y1', barY)
      .attr('x2', barEndX).attr('y2', barY)
      .attr('stroke', scaleColor).attr('stroke-width', style.branchWidth);

    [barStartX, barEndX].forEach((x) => {
      scaleGroup.append('line')
        .attr('x1', x).attr('y1', barY - 3)
        .attr('x2', x).attr('y2', barY + 3)
        .attr('stroke', scaleColor).attr('stroke-width', style.branchWidth);
    });

    scaleGroup.append('text')
      .attr('x', (barStartX + barEndX) / 2)
      .attr('y', barY - 6)
      .attr('text-anchor', 'middle')
      .attr('font-size', style.internalLabelSize + 'px')
      .attr('font-family', style.fontFamily)
      .attr('fill', scaleColor)
      .text(scaleBarValue < 0.001 ? scaleBarValue.toExponential(0) : String(scaleBarValue));
  }

  private getMaxBranchLengthFromTree(node: TreeNode): number {
    if (node.children.length === 0) return 0;
    return Math.max(
      ...node.children.map((child) => (child.branchLength ?? 1) + this.getMaxBranchLengthFromTree(child))
    );
  }

  private niceRound(value: number): number {
    const exp = Math.floor(Math.log10(value));
    const base = Math.pow(10, exp);
    const normalized = value / base;
    if (normalized < 1.5) return base;
    if (normalized < 3.5) return 2 * base;
    if (normalized < 7.5) return 5 * base;
    return 10 * base;
  }

  private getMetadataRow(name: string): Record<string, string> | null {
    const { metadataTable, metadataIdColumn } = this.options;
    if (!metadataTable) return null;
    const idCol = metadataIdColumn ?? metadataTable.headers[0];
    for (const row of metadataTable.rows) {
      const id = row[idCol];
      if (id === name || id === name.replace(/_/g, ' ') || id === name.replace(/ /g, '_')) {
        return row;
      }
    }
    return null;
  }

  private showTooltip(event: MouseEvent, node: TreeNode): void {
    this.hideTooltip();
    const dnm = this.options.tipColorMap?.displayNameByTip;
    const name = dnm
      ? (dnm.get(node.name) ?? dnm.get(node.name.replace(/_/g, ' ')) ?? dnm.get(node.name.replace(/ /g, '_')) ?? node.name.replace(/_/g, ' '))
      : node.name.replace(/_/g, ' ');
    let html = `<div class="tip-tooltip-name">${escapeHtml(name)}</div>`;
    if (node.branchLength !== null) {
      html += `<div class="tip-tooltip-row"><span class="tip-tooltip-key">Branch length</span><span>${node.branchLength}</span></div>`;
    }
    const row = this.getMetadataRow(node.name);
    if (row) {
      for (const header of this.options.metadataTable!.headers) {
        const val = row[header];
        if (val) {
          html += `<div class="tip-tooltip-row"><span class="tip-tooltip-key">${escapeHtml(header)}</span><span>${escapeHtml(val)}</span></div>`;
        }
      }
    }
    const tip = document.createElement('div');
    tip.className = 'tip-tooltip';
    tip.innerHTML = html;
    document.body.appendChild(tip);
    this.tooltip = tip;
    this.moveTooltip(event);
  }

  private moveTooltip(event: MouseEvent): void {
    if (!this.tooltip) return;
    const pad = 12;
    let x = event.clientX + pad;
    let y = event.clientY + pad;
    const rect = this.tooltip.getBoundingClientRect();
    if (x + rect.width > window.innerWidth) x = event.clientX - rect.width - pad;
    if (y + rect.height > window.innerHeight) y = event.clientY - rect.height - pad;
    this.tooltip.style.left = x + 'px';
    this.tooltip.style.top = y + 'px';
  }

  private hideTooltip(): void {
    if (this.tooltip) { this.tooltip.remove(); this.tooltip = null; }
  }

  private drawConnections(
    leftEnds: Map<string, { x: number; y: number }>,
    rightEnds: Map<string, { x: number; y: number }>,
    ts: TanglegramStyle
  ): void {
    const connGroup = this.g.append('g').attr('class', 'connections');

    const matchingNames = [...leftEnds.keys()].filter((name) => rightEnds.has(name));
    const colorScale = d3.scaleOrdinal(d3.schemeTableau10);

    // Compute stroke-dasharray for line style
    let dasharray: string | undefined;
    switch (ts.connectionLineStyle) {
      case 'dashed': dasharray = '6,4'; break;
      case 'dotted': dasharray = '2,3'; break;
      default: dasharray = undefined;
    }

    matchingNames.forEach((name, i) => {
      const left = leftEnds.get(name)!;
      const right = rightEnds.get(name)!;

      const midX = (left.x + right.x) / 2;
      const strokeColor = ts.connectionColorMode === 'single'
        ? ts.connectionColor
        : colorScale(String(i));

      const path = connGroup.append('path')
        .attr('class', 'connection')
        .attr('d', `M${left.x},${left.y} C${midX},${left.y} ${midX},${right.y} ${right.x},${right.y}`)
        .attr('fill', 'none')
        .attr('stroke', strokeColor)
        .attr('stroke-width', ts.connectionWidth)
        .attr('stroke-opacity', 0.6);

      if (dasharray) {
        path.attr('stroke-dasharray', dasharray);
      }
    });
  }

  getTransform(): d3.ZoomTransform {
    return d3.zoomTransform(this.svg.node()!);
  }

  setTransform(t: d3.ZoomTransform): void {
    this.svg.call(this.zoom.transform, t);
  }

  fitToView(): void {
    const containerRect = this.container.getBoundingClientRect();
    const cw = containerRect.width;
    const ch = containerRect.height;
    const scale = 0.85;
    const tx = cw * (1 - scale) / 2;
    const ty = ch * (1 - scale) / 2;

    this.svg.transition().duration(300).call(
      this.zoom.transform,
      d3.zoomIdentity.translate(tx, ty).scale(scale)
    );
  }

  setBoxSelectMode(enabled: boolean): void {
    this.boxSelectMode = enabled;
    if (enabled) {
      this.svg.style('cursor', 'crosshair');
    } else {
      this.svg.node()!.style.removeProperty('cursor');
      this.selectedNodes1.clear();
      this.selectedNodes2.clear();
      this.updateNodeHighlights();
      this.boxSelectStart = null;
      this.boxSelectRectEl?.attr('display', 'none');
      this.dismissContextMenu();
    }
  }

  private getSvgPos(e: MouseEvent): { x: number; y: number } {
    const rect = this.svg.node()!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  private onSvgBoxSelectMouseDown(e: MouseEvent): void {
    if (!this.boxSelectMode || e.button !== 0) return;
    e.preventDefault();
    this.dismissContextMenu();
    const pos = this.getSvgPos(e);
    this.boxSelectStart = { svgX: pos.x, svgY: pos.y };
    this.boxSelectRectEl?.attr('display', null)
      .attr('x', pos.x).attr('y', pos.y)
      .attr('width', 0).attr('height', 0);
  }

  private onBoxSelectMouseMove(e: MouseEvent): void {
    if (!this.boxSelectMode || !this.boxSelectStart) return;
    const pos = this.getSvgPos(e);
    const x0 = Math.min(this.boxSelectStart.svgX, pos.x);
    const y0 = Math.min(this.boxSelectStart.svgY, pos.y);
    this.boxSelectRectEl
      ?.attr('x', x0).attr('y', y0)
      .attr('width', Math.abs(pos.x - this.boxSelectStart.svgX))
      .attr('height', Math.abs(pos.y - this.boxSelectStart.svgY));
  }

  private onBoxSelectMouseUp(e: MouseEvent): void {
    if (!this.boxSelectMode || !this.boxSelectStart) return;
    const pos = this.getSvgPos(e);
    const dx = Math.abs(pos.x - this.boxSelectStart.svgX);
    const dy = Math.abs(pos.y - this.boxSelectStart.svgY);
    const start = this.boxSelectStart;
    this.boxSelectStart = null;
    this.boxSelectRectEl?.attr('display', 'none');

    if (dx < 4 && dy < 4) {
      if (!e.shiftKey) {
        this.selectedNodes1.clear();
        this.selectedNodes2.clear();
        this.updateNodeHighlights();
      }
      return;
    }

    const t = d3.zoomTransform(this.svg.node()!);
    const [tx0, ty0] = t.invert([Math.min(start.svgX, pos.x), Math.min(start.svgY, pos.y)]);
    const [tx1, ty1] = t.invert([Math.max(start.svgX, pos.x), Math.max(start.svgY, pos.y)]);

    if (!e.shiftKey) {
      this.selectedNodes1 = new Set();
      this.selectedNodes2 = new Set();
    }
    if (this.lastLayout1) {
      for (const ln of this.lastLayout1.nodes) {
        if (ln.x >= tx0 && ln.x <= tx1 && ln.y >= ty0 && ln.y <= ty1) {
          this.selectedNodes1.add(ln.node);
        }
      }
    }
    if (this.lastLayout2) {
      for (const ln of this.lastLayout2.nodes) {
        if (ln.x >= tx0 && ln.x <= tx1 && ln.y >= ty0 && ln.y <= ty1) {
          this.selectedNodes2.add(ln.node);
        }
      }
    }
    this.updateNodeHighlights();
  }

  private onBoxSelectKeyDown(e: KeyboardEvent): void {
    if (e.key === 'Escape' && this.boxSelectMode) {
      this.selectedNodes1.clear();
      this.selectedNodes2.clear();
      this.updateNodeHighlights();
      this.boxSelectStart = null;
      this.boxSelectRectEl?.attr('display', 'none');
      this.dismissContextMenu();
    }
  }

  private updateNodeHighlights(): void {
    const darkMode = !!this.options.darkMode;
    const selFill = darkMode ? 'rgba(100,180,255,0.22)' : 'rgba(0,94,162,0.18)';
    const selStroke = darkMode ? '#6ab4ff' : '#005ea2';
    const allSelected = new Set([...this.selectedNodes1, ...this.selectedNodes2]);
    this.g.selectAll<SVGCircleElement, { node: TreeNode }>('circle.node-target')
      .attr('fill', (d) => allSelected.has(d.node) ? selFill : 'transparent')
      .attr('stroke', (d) => allSelected.has(d.node) ? selStroke : 'transparent');
  }

  private showBulkContextMenu(x: number, y: number): void {
    this.dismissContextMenu();
    const count1 = this.selectedNodes1.size;
    const count2 = this.selectedNodes2.size;
    const total = count1 + count2;
    if (total === 0) return;

    const menu = document.createElement('div');
    menu.className = 'tree-context-menu';
    menu.style.left = x + 'px';
    menu.style.top = y + 'px';

    const addItem = (label: string, onClick: () => void): void => {
      const el = document.createElement('div');
      el.className = 'tree-context-menu-item tree-context-menu-item--danger';
      el.textContent = label;
      el.addEventListener('click', () => { this.dismissContextMenu(); onClick(); });
      menu.appendChild(el);
    };

    if (count1 > 0 && count2 > 0) {
      addItem(`Delete ${count1} node${count1 === 1 ? '' : 's'} from Tree 1`, () => this.executeBulkDelete(1));
      addItem(`Delete ${count2} node${count2 === 1 ? '' : 's'} from Tree 2`, () => this.executeBulkDelete(2));
    } else if (count1 > 0) {
      addItem(`Delete ${count1} selected node${count1 === 1 ? '' : 's'}`, () => this.executeBulkDelete(1));
    } else {
      addItem(`Delete ${count2} selected node${count2 === 1 ? '' : 's'}`, () => this.executeBulkDelete(2));
    }

    document.body.appendChild(menu);
    this.contextMenu = menu;

    requestAnimationFrame(() => {
      const rect = menu.getBoundingClientRect();
      if (rect.right > window.innerWidth) menu.style.left = (x - rect.width) + 'px';
      if (rect.bottom > window.innerHeight) menu.style.top = (y - rect.height) + 'px';
    });
  }

  private executeBulkDelete(treeIndex: 1 | 2): void {
    const onNodeEdit = this.options.onNodeEdit;
    if (!onNodeEdit) return;
    if (treeIndex === 1) {
      if (this.selectedNodes1.size === 0) return;
      const targets = [...this.selectedNodes1];
      this.selectedNodes1.clear();
      const newRoot = pruneNodes(this.options.tree1, targets);
      onNodeEdit(newRoot, 'prune', 1);
    } else {
      if (this.selectedNodes2.size === 0) return;
      const targets = [...this.selectedNodes2];
      this.selectedNodes2.clear();
      const newRoot = pruneNodes(this.options.tree2, targets);
      onNodeEdit(newRoot, 'prune', 2);
    }
  }

  destroy(): void {
    this.dismissContextMenu();
    this.hideTooltip();
    document.removeEventListener('click', this.dismissContextMenuBound);
    this.svg.node()?.removeEventListener('mousedown', this.onSvgMouseDownBound, true);
    window.removeEventListener('mousemove', this.onWindowMouseMoveBound);
    window.removeEventListener('mouseup', this.onWindowMouseUpBound);
    document.removeEventListener('keydown', this.onKeyDownBound);
    d3.select(this.container).selectAll('*').remove();
  }
}
